import { useState, useEffect, useCallback } from 'react';
import {
  EditOutlined,
  PlusCircleOutlined,
  ProfileOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import { IFinancialPlan, ICovenant } from '../../shared/interfaces';
import { useToast } from '../../hooks/toast';
import {
  ATable,
  MenuOptions,
  StatusActive,
  DropdownOptions,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../components';
import CovenantCreateEdit from './CreateEdit';
import { getCovenants } from '../../http';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';

const Covenants: React.FC = () => {
  const { addToast } = useToast();
  const { permissionFind } = useAuth();
  const [search, setSearch] = useState<string>('');
  const [covenant, setCovenant] = useState<ICovenant | null>(null);
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [covenants, setCovenants] = useState<ICovenant[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const list = useCallback(async () => {
    setLoading(true);
    setCovenants([]);
    try {
      const response = await getCovenants(limit, (page - 1) * limit, search);
      setPages(response.count);
      setCovenants(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar planos de contas',
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast]);

  const renderModalCovenantsCreateEdit = () => {
    if (modalCreate)
      return (
        <CovenantCreateEdit
          open={modalCreate}
          close={() => setModalCreate(false)}
          covenant={covenant}
          setCovenants={setCovenants}
        />
      );
  };

  useEffect(() => {
    list();
  }, [page, search, limit, list]);

  return (
    <PageLayout>
      <PageIntro
        badge="Cadastros"
        icon={<ProfileOutlined />}
        title="Convênios"
        description="Gerencie a base de convênios da clínica com uma listagem mais clara e acesso direto às ações principais."
      />
      <MenuOptions
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => list(),
          },
          permissionFind([PermissionActions.covenantsCreate])
            ? {
                label: 'Cadastrar',
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => {
                  setCovenant(null);
                  setModalCreate(true);
                },
              }
            : null,
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <PagePanel>
        <ATable
          columns={[
            {
              title: 'Id',
              width: 100,
              render: (value, record, index) => <>{index + 1}</>,
            },
            {
              title: 'Descrição',
              dataIndex: 'description',
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 95,
              render: (value: boolean) => <StatusActive status={value} />,
            },
            {
              title: 'Opções',
              width: 130,
              render: (cell: IFinancialPlan) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.covenantsUpdate])
                      ? {
                          label: 'Editar',
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => {
                            setCovenant(cell);
                            setModalCreate(true);
                          },
                        }
                      : null,
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={covenants}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
      {renderModalCovenantsCreateEdit()}
    </PageLayout>
  );
};

export default Covenants;
